import { useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import type { NavItem } from '../content/types';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  items: NavItem[];
  onOpenContact: () => void;
}

export function MobileMenu({ isOpen, onClose, items, onOpenContact }: MobileMenuProps) {
  const prefersReducedMotion = useReducedMotion();
  const drawerRef = useRef<HTMLDivElement>(null);

  useFocusTrap(drawerRef, isOpen, onClose);

  function handleScrollTo(sectionId: string | undefined) {
    onClose();
    if (!sectionId) return;
    const el = document.getElementById(sectionId);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      el.focus({ preventScroll: true });
    });
  }

  function handleContact() {
    onClose();
    requestAnimationFrame(() => onOpenContact());
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
        >
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="absolute top-0 right-0 h-full w-full max-w-sm bg-slate-dark border-l border-textlight/10 shadow-2xl flex flex-col"
            initial={prefersReducedMotion ? false : { x: '100%' }}
            animate={{ x: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { x: '100%' }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.28, ease: 'easeOut' }}
          >
            <div className="flex justify-end p-4">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="text-textlight/70 hover:text-textlight transition-colors p-3 -mr-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <XIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>

            <nav aria-label="Primary mobile" className="flex-1 px-6 pb-10 overflow-y-auto">
              <ul className="flex flex-col gap-2">
                {items.map((item) => (
                  <li key={item.label}>
                    {item.action === 'contact' ? (
                      <button
                        type="button"
                        onClick={handleContact}
                        className="w-full text-left py-4 font-serif text-2xl text-white hover:text-bronze transition-colors min-h-[44px] border-b border-textlight/10"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleScrollTo(item.sectionId)}
                        className="w-full text-left py-4 font-serif text-2xl text-white hover:text-bronze transition-colors min-h-[44px] border-b border-textlight/10"
                      >
                        {item.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
